import { useMemo } from 'react';

import { FinanceRate, WorkType } from '@kanda-libs/ks-frontend-services';

import { useCurrentJob, useCurrentJobLead } from 'hooks';
import { formatter, getFinanceOptions, getWorkType } from 'pages/Quote/helpers';

export interface NoApplicableLoansHook {
  maxLoan: string;
  maxDeposit: string;
  jobTotal: string;
  deficit: string;
  workTypeNotAllowed: boolean;
}

function useNoApplicableLoans(): NoApplicableLoansHook {
  // Get job
  const { job, isLoading: jobIsLoading } = useCurrentJob();
  // Get lead
  const { lead, isLoading: leadIsLoading } = useCurrentJobLead();

  const isLoading = jobIsLoading || leadIsLoading;

  const workTypeNotAllowed = useMemo(() => {
    if (!isLoading) return false;
    const rates = getFinanceOptions(job, lead, true);
    const workType = getWorkType(job, lead);
    const ratesWorkTypes = rates
      .map((rate: FinanceRate) => rate?.work_types || [])
      .flat()
      .filter(
        (type: WorkType, index: number, array: WorkType[]) =>
          array.indexOf(type) === index,
      );
    if (!workType || ratesWorkTypes.length === 0) return false;
    return ratesWorkTypes.includes(workType);
  }, [isLoading, job, lead]);

  const values = useMemo(() => {
    if (!isLoading)
      return {
        maxLoan: formatter('0'),
        maxDeposit: formatter('0'),
        jobTotal: formatter('0'),
        deficit: formatter('0'),
      };
    const rates = getFinanceOptions(job, lead, true);
    const jobTotal = job?.total?.amount_vat_inclusive?.amount || 0;
    const maxLoan =
      lead?.max_credit_amount?.amount ||
      rates
        ?.map((option: FinanceRate) => option.max_job_value || 0)
        ?.sort((max1: number, max2: number) => max2 - max1)?.[0] ||
      0;
    const maxDepositPct =
      (rates
        ?.map((option: FinanceRate) => option.max_deposit_pct || 0)
        ?.sort((max1: number, max2: number) => max1 - max2)?.[0] || 7500) / 100;
    const maxDeposit = (maxDepositPct / 100) * jobTotal;
    const deficit = Math.abs(jobTotal - maxDeposit - maxLoan);
    return {
      maxLoan: formatter(String(maxLoan)),
      maxDeposit: formatter(String(maxDeposit)),
      jobTotal: formatter(String(jobTotal)),
      deficit: formatter(String(deficit)),
    };
  }, [job, lead, isLoading]);

  return {
    ...values,
    workTypeNotAllowed,
  };
}

export default useNoApplicableLoans;
